import React, { useCallback } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// ── Brand colors ──
const BRAND = {
  primary: '#0950A0',
  primaryLight: '#3B7DD8',
  primaryBg: '#EBF2FA',
  dark: '#1B2A4A',
  gray: '#58595B',
  grayLight: '#F7F8FA',
  grayMid: '#E5E7EB',
  white: '#FFFFFF',
  accent: '#10B981',
  accentOrange: '#F59E0B',
  accentRed: '#EF4444',
  accentPurple: '#8B5CF6',
};

const UTILITY_COLORS = {
  electric: '#0950A0',
  gas: '#F59E0B',
  water: '#3B82F6',
  other: '#8B5CF6',
};

// ── Helper: format "2025-01" → "Jan 25" ──
const formatMonthLabel = (monthStr) => {
  if (!monthStr || monthStr.length < 7) return monthStr || '';
  const [year, month] = monthStr.split('-');
  const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${names[parseInt(month) - 1]} ${year.slice(-2)}`;
};

// ── Helper: format currency ──
const fmtCurrency = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtNumber = (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

// ── Helper: hex to RGB ──
const hexToRgb = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
};

// ── PDF layout constants (A4 landscape for bento) ──
const PAGE = {
  w: 279.4, // mm (11 in)
  h: 215.9, // mm (8.5 in)
  margin: 12,
  gutter: 6,
};
const CONTENT_W = PAGE.w - PAGE.margin * 2;
const CONTENT_H = PAGE.h - PAGE.margin * 2 - 20; // leave room for header/footer

// ── Bento cell positions (2-col or 3-col grid) ──
const col2W = (CONTENT_W - PAGE.gutter) / 2;
const _col3W = (CONTENT_W - PAGE.gutter * 2) / 3;

// ═══════════════════════════════════════════
// PDF-native chart drawing functions
// (draws directly into jsPDF — no html2canvas)
// ═══════════════════════════════════════════

function drawChartGrid(doc, px, py, pw, ph, { yMin = 0, yMax = 100, ySteps = 4, yFormat = (v) => String(Math.round(v)), xLabels = [] }) {
  // Subtle contrasting background for chart area
  doc.setFillColor(250, 251, 253);
  doc.rect(px, py, pw, ph, 'F');

  // Horizontal grid lines + Y-axis labels
  doc.setFontSize(5.5);
  doc.setFont('helvetica', 'normal');

  for (let i = 0; i <= ySteps; i++) {
    const ratio = i / ySteps;
    const lineY = py + ph - ratio * ph;
    const val = yMin + ratio * (yMax - yMin);

    doc.setDrawColor(215, 218, 224);
    doc.setLineWidth(0.25);
    doc.line(px, lineY, px + pw, lineY);

    doc.setTextColor(BRAND.gray);
    doc.text(yFormat(val), px - 1.5, lineY + 1.2, { align: 'right' });
  }

  // X-axis labels
  if (xLabels.length > 0) {
    const maxLabels = Math.floor(pw / 10);
    const interval = Math.max(1, Math.ceil(xLabels.length / maxLabels));

    xLabels.forEach((label, i) => {
      if (i % interval === 0 || i === xLabels.length - 1) {
        const xPos = xLabels.length === 1
          ? px + pw / 2
          : px + (i / (xLabels.length - 1)) * pw;
        doc.setTextColor(BRAND.gray);
        doc.setFontSize(5.5);
        doc.text(String(label), xPos, py + ph + 5, { align: 'center' });
      }
    });
  }
}

function drawPdfAreaChart(doc, cx, cy, cw, ch, data, { valueKey = 'cost', labelKey = 'name', color = BRAND.primary, yPrefix = '$' } = {}) {
  if (!data || data.length === 0) return;

  const values = data.map(d => d[valueKey] || 0);
  const maxVal = Math.max(...values);
  const yMax = maxVal > 0 ? Math.ceil(maxVal * 1.15) : 100;
  const labels = data.map(d => d[labelKey] || '');

  const px = cx + 14;
  const py = cy + 2;
  const pw = cw - 18;
  const ph = ch - 12;

  const yFormat = (v) => {
    if (v >= 1000000) return `${yPrefix}${(v / 1000000).toFixed(1)}M`;
    if (v >= 1000) return `${yPrefix}${(v / 1000).toFixed(0)}k`;
    return `${yPrefix}${Math.round(v)}`;
  };

  drawChartGrid(doc, px, py, pw, ph, { yMin: 0, yMax, ySteps: 4, yFormat, xLabels: labels });

  // Build points
  const points = data.map((d, i) => {
    const xRatio = data.length === 1 ? 0.5 : i / (data.length - 1);
    return {
      x: px + xRatio * pw,
      y: py + ph - ((d[valueKey] || 0) / yMax) * ph,
    };
  });

  // Area fill (blended with white for translucent effect)
  const [cr, cg, cb] = hexToRgb(color);
  const fillR = Math.round(cr * 0.12 + 255 * 0.88);
  const fillG = Math.round(cg * 0.12 + 255 * 0.88);
  const fillB = Math.round(cb * 0.12 + 255 * 0.88);
  doc.setFillColor(fillR, fillG, fillB);
  doc.setLineWidth(0.1);
  doc.setDrawColor(fillR, fillG, fillB);

  for (let i = 0; i < points.length - 1; i++) {
    const x1 = points[i].x;
    const x2 = points[i + 1].x;
    const midY = (points[i].y + points[i + 1].y) / 2;
    const botY = py + ph;
    if (x2 - x1 > 0) {
      doc.rect(x1, midY, x2 - x1, botY - midY, 'F');
    }
  }

  // Stroke line
  const [lr, lg, lb] = hexToRgb(color);
  doc.setDrawColor(lr, lg, lb);
  doc.setLineWidth(0.6);
  for (let i = 0; i < points.length - 1; i++) {
    doc.line(points[i].x, points[i].y, points[i + 1].x, points[i + 1].y);
  }

  // Dots
  doc.setFillColor(lr, lg, lb);
  points.forEach(p => doc.circle(p.x, p.y, 0.8, 'F'));
}

function drawPdfBarChart(doc, cx, cy, cw, ch, data, { valueKey = 'usage', labelKey = 'name', color = BRAND.primary, unit = '' } = {}) {
  if (!data || data.length === 0) return;

  const values = data.map(d => d[valueKey] || 0);
  const maxVal = Math.max(...values);
  const yMax = maxVal > 0 ? Math.ceil(maxVal * 1.15) : 100;
  const labels = data.map(d => d[labelKey] || '');

  const px = cx + 14;
  const py = cy + 2;
  const pw = cw - 18;
  const ph = ch - 12;

  const yFormat = (v) => {
    if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
    if (v >= 1000) return `${(v / 1000).toFixed(0)}k`;
    return String(Math.round(v));
  };

  drawChartGrid(doc, px, py, pw, ph, { yMin: 0, yMax, ySteps: 4, yFormat, xLabels: labels });

  // Draw bars
  const barAreaW = pw / data.length;
  const barW = Math.min(barAreaW * 0.6, 6);
  const [br, bg, bb] = hexToRgb(color);
  doc.setFillColor(br, bg, bb);

  data.forEach((d, i) => {
    const val = d[valueKey] || 0;
    if (val <= 0) return;
    const barH = (val / yMax) * ph;
    const bx = px + (i + 0.5) * barAreaW - barW / 2;
    const by = py + ph - barH;
    doc.roundedRect(bx, by, barW, barH, 0.8, 0.8, 'F');
  });

  // Unit label at top-left
  if (unit) {
    doc.setFontSize(5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(BRAND.gray);
    doc.text(unit, cx + 1, cy + 5);
  }
}

function drawPdfGroupedBarChart(doc, cx, cy, cw, ch, data, {
  key1 = 'currentYear', key2 = 'previousYear', labelKey = 'month',
  color1 = BRAND.primary, color2 = BRAND.primaryLight,
  name1 = 'Current Year', name2 = 'Previous Year',
  yPrefix = '$',
} = {}) {
  if (!data || data.length === 0) return;

  const allValues = data.flatMap(d => [d[key1] || 0, d[key2] || 0]);
  const maxVal = Math.max(...allValues);
  const yMax = maxVal > 0 ? Math.ceil(maxVal * 1.15) : 100;
  const labels = data.map(d => d[labelKey] || '');

  const px = cx + 14;
  const py = cy + 6; // extra room for legend at top
  const pw = cw - 18;
  const ph = ch - 16;

  const yFormat = (v) => {
    if (v >= 1000000) return `${yPrefix}${(v / 1000000).toFixed(1)}M`;
    if (v >= 1000) return `${yPrefix}${(v / 1000).toFixed(0)}k`;
    return `${yPrefix}${Math.round(v)}`;
  };

  drawChartGrid(doc, px, py, pw, ph, { yMin: 0, yMax, ySteps: 4, yFormat, xLabels: labels });

  // Grouped bars
  const groupW = pw / data.length;
  const barW = Math.min(groupW * 0.3, 4);
  const gap = 0.8;

  data.forEach((d, i) => {
    const centerX = px + (i + 0.5) * groupW;

    const val1 = d[key1] || 0;
    if (val1 > 0) {
      const h1 = (val1 / yMax) * ph;
      const [r1, g1, b1] = hexToRgb(color1);
      doc.setFillColor(r1, g1, b1);
      doc.roundedRect(centerX - barW - gap / 2, py + ph - h1, barW, h1, 0.6, 0.6, 'F');
    }

    const val2 = d[key2] || 0;
    if (val2 > 0) {
      const h2 = (val2 / yMax) * ph;
      const [r2, g2, b2] = hexToRgb(color2);
      doc.setFillColor(r2, g2, b2);
      doc.roundedRect(centerX + gap / 2, py + ph - h2, barW, h2, 0.6, 0.6, 'F');
    }
  });

  // Legend
  const legendY = cy + 1;
  const legendX = cx + cw - 55;
  doc.setFontSize(5.5);
  doc.setFont('helvetica', 'normal');

  const [lr1, lg1, lb1] = hexToRgb(color1);
  doc.setFillColor(lr1, lg1, lb1);
  doc.rect(legendX, legendY, 2.5, 2.5, 'F');
  doc.setTextColor(BRAND.dark);
  doc.text(name1, legendX + 4, legendY + 2);

  const [lr2, lg2, lb2] = hexToRgb(color2);
  doc.setFillColor(lr2, lg2, lb2);
  doc.rect(legendX + 28, legendY, 2.5, 2.5, 'F');
  doc.text(name2, legendX + 32, legendY + 2);
}

function drawPdfMultiLineChart(doc, cx, cy, cw, ch, data, { utilityTypes = [], labelKey = 'name', yPrefix = '$' } = {}) {
  if (!data || data.length === 0 || utilityTypes.length === 0) return;

  const allValues = data.flatMap(d => utilityTypes.map(ut => d[ut] || 0));
  const maxVal = Math.max(...allValues);
  const yMax = maxVal > 0 ? Math.ceil(maxVal * 1.15) : 100;
  const labels = data.map(d => d[labelKey] || '');

  const px = cx + 14;
  const py = cy + 6;
  const pw = cw - 18;
  const ph = ch - 16;

  const yFormat = (v) => {
    if (v >= 1000000) return `${yPrefix}${(v / 1000000).toFixed(1)}M`;
    if (v >= 1000) return `${yPrefix}${(v / 1000).toFixed(0)}k`;
    return `${yPrefix}${Math.round(v)}`;
  };

  drawChartGrid(doc, px, py, pw, ph, { yMin: 0, yMax, ySteps: 4, yFormat, xLabels: labels });

  // Draw a line for each utility type
  utilityTypes.forEach(ut => {
    const color = UTILITY_COLORS[ut] || '#8B5CF6';
    const [lr, lg, lb] = hexToRgb(color);

    const points = data.map((d, i) => {
      const xRatio = data.length === 1 ? 0.5 : i / (data.length - 1);
      return {
        x: px + xRatio * pw,
        y: py + ph - ((d[ut] || 0) / yMax) * ph,
      };
    });

    doc.setDrawColor(lr, lg, lb);
    doc.setLineWidth(0.5);
    for (let i = 0; i < points.length - 1; i++) {
      doc.line(points[i].x, points[i].y, points[i + 1].x, points[i + 1].y);
    }

    doc.setFillColor(lr, lg, lb);
    points.forEach(p => doc.circle(p.x, p.y, 0.6, 'F'));
  });

  // Legend
  const legendY = cy + 1;
  let legendX = cx + cw - utilityTypes.length * 22;
  doc.setFontSize(5.5);
  doc.setFont('helvetica', 'normal');
  utilityTypes.forEach(ut => {
    const color = UTILITY_COLORS[ut] || '#8B5CF6';
    const [lr, lg, lb] = hexToRgb(color);
    doc.setFillColor(lr, lg, lb);
    doc.rect(legendX, legendY, 2.5, 2.5, 'F');
    doc.setTextColor(BRAND.dark);
    doc.text(ut.charAt(0).toUpperCase() + ut.slice(1), legendX + 4, legendY + 2);
    legendX += 22;
  });
}

// ═══════════════════════════════════════════
// Data aggregation helpers
// ═══════════════════════════════════════════

function aggregateFacilityData(monthlyStats, facilityName) {
  const stats = monthlyStats.filter(s => s.facility === facilityName);
  const months = [...new Set(stats.map(s => s.month))].sort();
  const utilityTypes = [...new Set(stats.map(s => s.utility))].sort();

  const totalCost = stats.reduce((s, st) => s + (st.totalCost || 0), 0);
  const totalUsage = stats.reduce((s, st) => s + (st.totalUsage || 0), 0);
  const billCount = stats.reduce((s, st) => s + (st.count || 0), 0);

  // Cost chart data (aggregated across utilities by month)
  const costMap = {};
  stats.forEach(st => {
    if (!costMap[st.month]) costMap[st.month] = 0;
    costMap[st.month] += st.totalCost || 0;
  });
  const costChartData = Object.keys(costMap).sort().map(m => ({
    name: formatMonthLabel(m),
    sortKey: m,
    cost: costMap[m],
  }));

  // Usage chart data (per utility type)
  const usageByUtility = {};
  utilityTypes.forEach(ut => {
    const uStats = stats.filter(s => s.utility === ut);
    usageByUtility[ut] = {
      unit: uStats[0]?.unit || 'units',
      data: uStats.sort((a, b) => a.month.localeCompare(b.month)).map(s => ({
        name: formatMonthLabel(s.month),
        usage: s.totalUsage || 0,
      })),
      total: uStats.reduce((s, st) => s + (st.totalUsage || 0), 0),
    };
  });

  // Cost breakdown by utility
  const costByUtility = {};
  utilityTypes.forEach(ut => {
    costByUtility[ut] = stats.filter(s => s.utility === ut).reduce((s, st) => s + (st.totalCost || 0), 0);
  });

  return { totalCost, totalUsage, billCount, months, utilityTypes, costChartData, usageByUtility, costByUtility, stats };
}

function buildMonthlyComparisonData(monthlyStats, facilityName) {
  const stats = facilityName
    ? monthlyStats.filter(s => s.facility === facilityName)
    : monthlyStats;

  const now = new Date();
  const currentYear = now.getFullYear();
  const previousYear = currentYear - 1;

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Cost comparison
  const costComparison = monthNames.map((mName, idx) => {
    const mNum = String(idx + 1).padStart(2, '0');
    const curKey = `${currentYear}-${mNum}`;
    const prevKey = `${previousYear}-${mNum}`;

    const curCost = stats.filter(s => s.month === curKey).reduce((s, st) => s + (st.totalCost || 0), 0);
    const prevCost = stats.filter(s => s.month === prevKey).reduce((s, st) => s + (st.totalCost || 0), 0);

    return { month: mName, currentYear: curCost, previousYear: prevCost };
  });

  // Usage comparison (aggregate across utility types)
  const usageComparison = monthNames.map((mName, idx) => {
    const mNum = String(idx + 1).padStart(2, '0');
    const curKey = `${currentYear}-${mNum}`;
    const prevKey = `${previousYear}-${mNum}`;

    const curUsage = stats.filter(s => s.month === curKey).reduce((s, st) => s + (st.totalUsage || 0), 0);
    const prevUsage = stats.filter(s => s.month === prevKey).reduce((s, st) => s + (st.totalUsage || 0), 0);

    return { month: mName, currentYear: curUsage, previousYear: prevUsage };
  });

  // Multi-utility cost breakdown by month (for summary)
  const utilityTypes = [...new Set(stats.map(s => s.utility))].sort();
  const multiUtilityData = [...new Set(stats.map(s => s.month))].sort().map(m => {
    const entry = { name: formatMonthLabel(m) };
    utilityTypes.forEach(ut => {
      entry[ut] = stats.filter(s => s.month === m && s.utility === ut).reduce((s, st) => s + (st.totalCost || 0), 0);
    });
    return entry;
  });

  return { costComparison, usageComparison, multiUtilityData, utilityTypes, currentYear, previousYear };
}

function buildUsageComparisonByUtility(monthlyStats, facilityName, utilityType) {
  const stats = facilityName
    ? monthlyStats.filter(s => s.facility === facilityName && s.utility === utilityType)
    : monthlyStats.filter(s => s.utility === utilityType);

  const now = new Date();
  const currentYear = now.getFullYear();
  const previousYear = currentYear - 1;

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return monthNames.map((mName, idx) => {
    const mNum = String(idx + 1).padStart(2, '0');
    const curKey = `${currentYear}-${mNum}`;
    const prevKey = `${previousYear}-${mNum}`;

    const curUsage = stats.filter(s => s.month === curKey).reduce((s, st) => s + (st.totalUsage || 0), 0);
    const prevUsage = stats.filter(s => s.month === prevKey).reduce((s, st) => s + (st.totalUsage || 0), 0);

    return { month: mName, currentYear: curUsage, previousYear: prevUsage };
  });
}

// ═══════════════════════════════════════════
// PDF Drawing Helpers
// ═══════════════════════════════════════════

function drawBentoCard(doc, x, y, w, h, { title, bgColor = BRAND.white, borderColor = BRAND.grayMid }) {
  // Card background with rounded corners
  doc.setFillColor(bgColor);
  doc.setDrawColor(borderColor);
  doc.setLineWidth(0.4);
  doc.roundedRect(x, y, w, h, 3, 3, 'FD');

  // Title bar
  if (title) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(BRAND.dark);
    doc.text(title, x + 5, y + 8);
  }

  return { contentX: x + 4, contentY: y + (title ? 13 : 4), contentW: w - 8, contentH: h - (title ? 17 : 8) };
}

function drawKpiCard(doc, x, y, w, h, { label, value, subtext, accentColor = BRAND.primary }) {
  drawBentoCard(doc, x, y, w, h, { bgColor: BRAND.white });

  // Accent line on left
  doc.setDrawColor(accentColor);
  doc.setLineWidth(1.5);
  doc.line(x + 2, y + 4, x + 2, y + h - 4);

  // Label
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(BRAND.gray);
  doc.text(label, x + 7, y + 10);

  // Value
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(BRAND.dark);
  doc.text(String(value), x + 7, y + 22);

  // Subtext
  if (subtext) {
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(BRAND.gray);
    doc.text(subtext, x + 7, y + 28);
  }
}

function drawFacilityDivider(doc, y, facilityName, pageNum) {
  // Full-width gradient-style divider
  const x = PAGE.margin;
  const w = CONTENT_W;
  const h = 14;

  // Background bar
  doc.setFillColor(BRAND.dark);
  doc.roundedRect(x, y, w, h, 2, 2, 'F');

  // Facility name
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(BRAND.white);
  doc.text(facilityName, x + 8, y + 9.5);

  // Page indicator
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text(`Page ${pageNum}`, x + w - 25, y + 9.5);

  return y + h + 4;
}

function drawPageHeader(doc, logoData) {
  // Thin brand header line
  doc.setFillColor(BRAND.primary);
  doc.rect(0, 0, PAGE.w, 3, 'F');

  // Logo (if available) — positioned in top-right with space below for content
  if (logoData) {
    try {
      doc.addImage(logoData, 'JPEG', PAGE.w - PAGE.margin - 28, 4, 28, 12);
    } catch { /* skip if logo fails */ }
  }

  // Date stamp
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(BRAND.gray);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, PAGE.margin, 10);
}

function drawPageFooter(doc, pageNum, totalPages) {
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(BRAND.gray);
  doc.text(`UtiliTrack Report  ·  Page ${pageNum} of ${totalPages}`, PAGE.w / 2, PAGE.h - 5, { align: 'center' });
}

function drawSummaryDivider(doc, y) {
  // Section divider for summary
  const x = PAGE.margin;
  const w = CONTENT_W;
  const h = 12;

  doc.setFillColor(BRAND.primary);
  doc.roundedRect(x, y, w, h, 2, 2, 'F');

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(BRAND.white);
  doc.text('PORTFOLIO SUMMARY', x + 8, y + 8.5);

  return y + h + 4;
}

// ═══════════════════════════════════════════
// React Component
// ═══════════════════════════════════════════
const BentoReportExporter = React.forwardRef(({ monthlyStats, onProgress }, ref) => {
  // Build all facility data
  const facilities = [...new Set(monthlyStats.map(s => s.facility))].sort();

  // ── Core export method exposed via ref ──
  const generateBentoPDF = useCallback(async (options = {}) => {
    const {
      groupByFacility = true,
      includeSummaryPages = true,
      includeComparisonCharts = true,
      includeMonthlyComparison = true,
      selectedFacilities = null, // null = all
    } = options;

    onProgress?.('Preparing report data...');

    const targetFacilities = selectedFacilities || facilities;
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });

    // ── Load logo ──
    let logoData = null;
    try {
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      logoImg.src = '/logo_multi.png';
      await new Promise((resolve, _reject) => {
        logoImg.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = logoImg.width;
          canvas.height = logoImg.height;
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(logoImg, 0, 0);
          logoData = canvas.toDataURL('image/jpeg', 0.8);
          resolve();
        };
        logoImg.onerror = resolve; // skip if fails
      });
    } catch { /* skip logo */ }

    let pageCount = 0;
    const addNewPage = () => {
      if (pageCount > 0) doc.addPage();
      pageCount++;
      drawPageHeader(doc, logoData);
    };

    // ── Pre-compute summary chart data ──
    const summaryData = buildMonthlyComparisonData(monthlyStats, null);
    const allCostMap = {};
    monthlyStats.forEach(s => {
      if (!allCostMap[s.month]) allCostMap[s.month] = 0;
      allCostMap[s.month] += s.totalCost || 0;
    });
    const allCostChartData = Object.keys(allCostMap).sort().map(m => ({
      name: formatMonthLabel(m),
      cost: allCostMap[m],
    }));

    // ══════════════════════════════
    // SUMMARY PAGES
    // ══════════════════════════════
    if (includeSummaryPages) {
      onProgress?.('Building summary pages...');

      // ── Summary Page 1: KPIs + overview table ──
      addNewPage();
      let y = 18;
      y = drawSummaryDivider(doc, y);

      // KPI row (4 cards across)
      const kpiW = (CONTENT_W - PAGE.gutter * 3) / 4;
      const kpiH = 34;

      const allStats = monthlyStats;
      const grandTotalCost = allStats.reduce((s, st) => s + (st.totalCost || 0), 0);
      const _grandTotalUsage = allStats.reduce((s, st) => s + (st.totalUsage || 0), 0);
      const grandBillCount = allStats.reduce((s, st) => s + (st.count || 0), 0);
      const utilTypes = [...new Set(allStats.map(s => s.utility))].sort();

      drawKpiCard(doc, PAGE.margin, y, kpiW, kpiH, {
        label: 'Total Portfolio Spend',
        value: fmtCurrency(grandTotalCost),
        subtext: 'All facilities combined',
        accentColor: BRAND.primary,
      });
      drawKpiCard(doc, PAGE.margin + kpiW + PAGE.gutter, y, kpiW, kpiH, {
        label: 'Total Facilities',
        value: String(targetFacilities.length),
        subtext: 'Active locations',
        accentColor: BRAND.accent,
      });
      drawKpiCard(doc, PAGE.margin + (kpiW + PAGE.gutter) * 2, y, kpiW, kpiH, {
        label: 'Bills Analyzed',
        value: fmtNumber(grandBillCount),
        subtext: 'In reporting period',
        accentColor: BRAND.accentOrange,
      });
      drawKpiCard(doc, PAGE.margin + (kpiW + PAGE.gutter) * 3, y, kpiW, kpiH, {
        label: 'Utility Types',
        value: String(utilTypes.length),
        subtext: utilTypes.map(u => u.charAt(0).toUpperCase() + u.slice(1)).join(', '),
        accentColor: BRAND.accentPurple,
      });

      y += kpiH + PAGE.gutter;

      // Facility summary table (bento card)
      const tableH = CONTENT_H - (y - 18) - 4;
      const tableCard = drawBentoCard(doc, PAGE.margin, y, CONTENT_W, tableH, { title: 'Facility Cost & Usage Summary' });

      const summaryRows = targetFacilities.map(fac => {
        const facData = aggregateFacilityData(monthlyStats, fac);
        return [
          fac,
          String(facData.billCount),
          fmtCurrency(facData.totalCost),
          fmtNumber(facData.totalUsage),
          facData.utilityTypes.map(u => u.charAt(0).toUpperCase() + u.slice(1)).join(', '),
        ];
      });

      autoTable(doc, {
        head: [['Facility', 'Bills', 'Total Cost', 'Total Usage', 'Utility Types']],
        body: summaryRows,
        startY: tableCard.contentY,
        margin: { left: tableCard.contentX, right: PAGE.w - (tableCard.contentX + tableCard.contentW) },
        theme: 'grid',
        headStyles: {
          fillColor: BRAND.primary,
          textColor: '#ffffff',
          fontStyle: 'bold',
          fontSize: 9,
          cellPadding: 3,
        },
        bodyStyles: { fontSize: 8, cellPadding: 2.5 },
        alternateRowStyles: { fillColor: BRAND.grayLight },
        columnStyles: {
          0: { cellWidth: 55 },
          1: { cellWidth: 20, halign: 'center' },
          2: { cellWidth: 35, halign: 'right' },
          3: { cellWidth: 35, halign: 'right' },
          4: { cellWidth: 'auto' },
        },
      });

      // ── Summary Page 2: Comparison charts (if requested) ──
      if (includeComparisonCharts) {
        addNewPage();
        y = 18;

        // Title
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(BRAND.dark);
        doc.text('Portfolio Cost & Usage Overview', PAGE.margin, y + 6);
        y += 12;

        // 2-col layout: multi-utility cost chart + cost trend
        const chartH = 70;

        // Left: multi-utility line chart
        const card1 = drawBentoCard(doc, PAGE.margin, y, col2W, chartH, { title: 'Cost by Utility Type' });
        drawPdfMultiLineChart(doc, card1.contentX, card1.contentY, card1.contentW, card1.contentH, summaryData.multiUtilityData, {
          utilityTypes: summaryData.utilityTypes,
        });

        // Right: aggregate cost trend
        const card2 = drawBentoCard(doc, PAGE.margin + col2W + PAGE.gutter, y, col2W, chartH, { title: 'Total Cost Trend' });
        drawPdfAreaChart(doc, card2.contentX, card2.contentY, card2.contentW, card2.contentH, allCostChartData);

        y += chartH + PAGE.gutter;

        // Row 2: Year-over-year comparisons
        if (includeMonthlyComparison) {
          const card3 = drawBentoCard(doc, PAGE.margin, y, col2W, chartH, { title: 'Cost: Year-over-Year Comparison' });
          drawPdfGroupedBarChart(doc, card3.contentX, card3.contentY, card3.contentW, card3.contentH, summaryData.costComparison, {
            name1: String(summaryData.currentYear), name2: String(summaryData.previousYear),
          });

          // If only one utility type, show its usage comparison beside cost
          if (summaryData.utilityTypes.length === 1) {
            const ut = summaryData.utilityTypes[0];
            const utUnit = monthlyStats.find(s => s.utility === ut)?.unit || 'units';
            const utComp = buildUsageComparisonByUtility(monthlyStats, null, ut);
            const card4 = drawBentoCard(doc, PAGE.margin + col2W + PAGE.gutter, y, col2W, chartH, {
              title: `${ut.charAt(0).toUpperCase() + ut.slice(1)} Usage YoY (${utUnit})`,
            });
            drawPdfGroupedBarChart(doc, card4.contentX, card4.contentY, card4.contentW, card4.contentH, utComp, {
              name1: String(summaryData.currentYear), name2: String(summaryData.previousYear), yPrefix: '',
              color1: UTILITY_COLORS[ut] || BRAND.primary,
            });
          }

          y += chartH + PAGE.gutter;

          // Per-utility usage YoY charts (when multiple utility types)
          if (summaryData.utilityTypes.length > 1) {
            for (let sui = 0; sui < summaryData.utilityTypes.length; sui += 2) {
              if (y + chartH + 4 > CONTENT_H + 18) {
                addNewPage();
                y = 18;
              }

              const ut1 = summaryData.utilityTypes[sui];
              const utUnit1 = monthlyStats.find(s => s.utility === ut1)?.unit || 'units';
              const utComp1 = buildUsageComparisonByUtility(monthlyStats, null, ut1);
              const utCard1 = drawBentoCard(doc, PAGE.margin, y, col2W, chartH, {
                title: `${ut1.charAt(0).toUpperCase() + ut1.slice(1)} Usage YoY (${utUnit1})`,
              });
              drawPdfGroupedBarChart(doc, utCard1.contentX, utCard1.contentY, utCard1.contentW, utCard1.contentH, utComp1, {
                name1: String(summaryData.currentYear), name2: String(summaryData.previousYear), yPrefix: '',
                color1: UTILITY_COLORS[ut1] || BRAND.primary,
              });

              if (sui + 1 < summaryData.utilityTypes.length) {
                const ut2 = summaryData.utilityTypes[sui + 1];
                const utUnit2 = monthlyStats.find(s => s.utility === ut2)?.unit || 'units';
                const utComp2 = buildUsageComparisonByUtility(monthlyStats, null, ut2);
                const utCard2 = drawBentoCard(doc, PAGE.margin + col2W + PAGE.gutter, y, col2W, chartH, {
                  title: `${ut2.charAt(0).toUpperCase() + ut2.slice(1)} Usage YoY (${utUnit2})`,
                });
                drawPdfGroupedBarChart(doc, utCard2.contentX, utCard2.contentY, utCard2.contentW, utCard2.contentH, utComp2, {
                  name1: String(summaryData.currentYear), name2: String(summaryData.previousYear), yPrefix: '',
                  color1: UTILITY_COLORS[ut2] || BRAND.primary,
                });
              }

              y += chartH + PAGE.gutter;
            }
          }
        }
      }
    }

    // ══════════════════════════════
    // FACILITY PAGES
    // ══════════════════════════════
    if (groupByFacility) {
      for (let fi = 0; fi < targetFacilities.length; fi++) {
        const fac = targetFacilities[fi];
        onProgress?.(`Building page for ${fac} (${fi + 1}/${targetFacilities.length})...`);
        const facData = aggregateFacilityData(monthlyStats, fac);
        const compData = buildMonthlyComparisonData(monthlyStats, fac);

        // ── Facility page 1: KPIs + charts ──
        addNewPage();
        let y = 18;

        // Facility divider
        y = drawFacilityDivider(doc, y, fac, fi + 1);

        // KPI row (3 cards + 1 cost breakdown)
        const kpiW3 = (CONTENT_W - PAGE.gutter * 2) / 3;
        const kpiH = 30;

        drawKpiCard(doc, PAGE.margin, y, kpiW3, kpiH, {
          label: 'Total Spend',
          value: fmtCurrency(facData.totalCost),
          subtext: `${facData.billCount} bills`,
          accentColor: BRAND.primary,
        });

        // Utility cost breakdown card
        const breakdownCard = drawBentoCard(doc, PAGE.margin + kpiW3 + PAGE.gutter, y, kpiW3, kpiH, { title: 'Cost by Type' });
        let bx = breakdownCard.contentX;
        let by = breakdownCard.contentY + 2;
        doc.setFontSize(7);
        facData.utilityTypes.forEach(ut => {
          const cost = facData.costByUtility[ut] || 0;
          const pct = facData.totalCost > 0 ? ((cost / facData.totalCost) * 100).toFixed(0) : 0;
          // Draw colored square indicator instead of Unicode bullet
          const [ur, ug, ub] = hexToRgb(UTILITY_COLORS[ut] || BRAND.gray);
          doc.setFillColor(ur, ug, ub);
          doc.circle(bx + 1.2, by - 1, 1.2, 'F');
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(BRAND.dark);
          doc.text(ut.charAt(0).toUpperCase() + ut.slice(1), bx + 4, by);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(BRAND.dark);
          doc.text(`${fmtCurrency(cost)} (${pct}%)`, bx + 22, by);
          by += 4;
        });

        // Usage KPI — show per utility type instead of combined total
        const usageKpiCard = drawBentoCard(doc, PAGE.margin + (kpiW3 + PAGE.gutter) * 2, y, kpiW3, kpiH, { title: 'Usage by Type' });
        let ux = usageKpiCard.contentX;
        let uy = usageKpiCard.contentY + 2;
        doc.setFontSize(7);
        facData.utilityTypes.forEach(ut => {
          const usage = facData.usageByUtility[ut]?.total || 0;
          const unit = facData.usageByUtility[ut]?.unit || 'units';
          const [ur, ug, ub] = hexToRgb(UTILITY_COLORS[ut] || BRAND.gray);
          doc.setFillColor(ur, ug, ub);
          doc.circle(ux + 1.2, uy - 1, 1.2, 'F');
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(BRAND.dark);
          doc.text(ut.charAt(0).toUpperCase() + ut.slice(1), ux + 4, uy);
          doc.setFont('helvetica', 'normal');
          doc.text(`${fmtNumber(usage)} ${unit}`, ux + 22, uy);
          uy += 4;
        });

        y += kpiH + PAGE.gutter;

        // Charts: cost trend + one usage chart per utility type
        const chartH = 65;

        // Cost trend chart (drawn directly in PDF)
        const costCard = drawBentoCard(doc, PAGE.margin, y, col2W, chartH, { title: 'Monthly Cost Trend' });
        drawPdfAreaChart(doc, costCard.contentX, costCard.contentY, costCard.contentW, costCard.contentH, facData.costChartData);

        // Usage chart for first utility type
        if (facData.utilityTypes.length > 0) {
          const firstUt = facData.utilityTypes[0];
          const usageCard = drawBentoCard(doc, PAGE.margin + col2W + PAGE.gutter, y, col2W, chartH, {
            title: `${firstUt.charAt(0).toUpperCase() + firstUt.slice(1)} Usage (${facData.usageByUtility[firstUt]?.unit || 'units'})`,
          });
          drawPdfBarChart(doc, usageCard.contentX, usageCard.contentY, usageCard.contentW, usageCard.contentH,
            facData.usageByUtility[firstUt]?.data || [], { unit: facData.usageByUtility[firstUt]?.unit || 'units', color: UTILITY_COLORS[firstUt] || BRAND.primary });
        }

        y += chartH + PAGE.gutter;

        // Additional usage charts for remaining utility types
        if (facData.utilityTypes.length > 1) {
          const remainingTypes = facData.utilityTypes.slice(1);
          for (let ui = 0; ui < remainingTypes.length; ui += 2) {
            if (y + 65 > CONTENT_H + 18) {
              addNewPage();
              y = 18;
              y = drawFacilityDivider(doc, y, `${fac} (cont.)`, fi + 1);
            }

            const ut1 = remainingTypes[ui];
            const card1 = drawBentoCard(doc, PAGE.margin, y, col2W, chartH, {
              title: `${ut1.charAt(0).toUpperCase() + ut1.slice(1)} Usage (${facData.usageByUtility[ut1]?.unit || 'units'})`,
            });
            drawPdfBarChart(doc, card1.contentX, card1.contentY, card1.contentW, card1.contentH,
              facData.usageByUtility[ut1]?.data || [], { unit: facData.usageByUtility[ut1]?.unit || 'units', color: UTILITY_COLORS[ut1] || BRAND.primary });

            if (ui + 1 < remainingTypes.length) {
              const ut2 = remainingTypes[ui + 1];
              const card2 = drawBentoCard(doc, PAGE.margin + col2W + PAGE.gutter, y, col2W, chartH, {
                title: `${ut2.charAt(0).toUpperCase() + ut2.slice(1)} Usage (${facData.usageByUtility[ut2]?.unit || 'units'})`,
              });
              drawPdfBarChart(doc, card2.contentX, card2.contentY, card2.contentW, card2.contentH,
                facData.usageByUtility[ut2]?.data || [], { unit: facData.usageByUtility[ut2]?.unit || 'units', color: UTILITY_COLORS[ut2] || BRAND.primary });
            }

            y += chartH + PAGE.gutter;
          }
        }

        // ── Month-to-month comparison charts for facility ──
        if (includeMonthlyComparison) {
          if (y + 70 > CONTENT_H + 18) {
            addNewPage();
            y = 18;
            y = drawFacilityDivider(doc, y, `${fac} (cont.)`, fi + 1);
          }

          const compCostCard = drawBentoCard(doc, PAGE.margin, y, col2W, chartH, { title: 'Cost: Month-over-Month Comparison' });
          drawPdfGroupedBarChart(doc, compCostCard.contentX, compCostCard.contentY, compCostCard.contentW, compCostCard.contentH, compData.costComparison, {
            name1: String(compData.currentYear), name2: String(compData.previousYear),
          });

          // Usage comparison per utility type (separate chart per utility)
          y += chartH + PAGE.gutter;

          for (let uci = 0; uci < facData.utilityTypes.length; uci += 2) {
            if (y + 70 > CONTENT_H + 18) {
              addNewPage();
              y = 18;
              y = drawFacilityDivider(doc, y, `${fac} (cont.)`, fi + 1);
            }

            const ut1 = facData.utilityTypes[uci];
            const utComp1 = buildUsageComparisonByUtility(monthlyStats, fac, ut1);
            const utUnit1 = facData.usageByUtility[ut1]?.unit || 'units';
            const utCompCard1 = drawBentoCard(doc, PAGE.margin, y, col2W, chartH, {
              title: `${ut1.charAt(0).toUpperCase() + ut1.slice(1)} Usage YoY (${utUnit1})`,
            });
            drawPdfGroupedBarChart(doc, utCompCard1.contentX, utCompCard1.contentY, utCompCard1.contentW, utCompCard1.contentH, utComp1, {
              name1: String(compData.currentYear), name2: String(compData.previousYear), yPrefix: '',
              color1: UTILITY_COLORS[ut1] || BRAND.primary,
            });

            if (uci + 1 < facData.utilityTypes.length) {
              const ut2 = facData.utilityTypes[uci + 1];
              const utComp2 = buildUsageComparisonByUtility(monthlyStats, fac, ut2);
              const utUnit2 = facData.usageByUtility[ut2]?.unit || 'units';
              const utCompCard2 = drawBentoCard(doc, PAGE.margin + col2W + PAGE.gutter, y, col2W, chartH, {
                title: `${ut2.charAt(0).toUpperCase() + ut2.slice(1)} Usage YoY (${utUnit2})`,
              });
              drawPdfGroupedBarChart(doc, utCompCard2.contentX, utCompCard2.contentY, utCompCard2.contentW, utCompCard2.contentH, utComp2, {
                name1: String(compData.currentYear), name2: String(compData.previousYear), yPrefix: '',
                color1: UTILITY_COLORS[ut2] || BRAND.primary,
              });
            }

            y += chartH + PAGE.gutter;
          }
        }

        // ── Bill detail table ──
        if (y + 40 > CONTENT_H + 18) {
          addNewPage();
          y = 18;
          y = drawFacilityDivider(doc, y, `${fac} – Bill Details`, fi + 1);
        }

        const detailTableH = Math.max(40, CONTENT_H + 18 - y - 4);
        const dtCard = drawBentoCard(doc, PAGE.margin, y, CONTENT_W, detailTableH, { title: 'Bill Summary by Utility & Month' });

        // Build condensed table from stats
        const detailRows = [];
        facData.utilityTypes.forEach(ut => {
          const uStats = facData.stats.filter(s => s.utility === ut).sort((a, b) => a.month.localeCompare(b.month));
          uStats.forEach(s => {
            detailRows.push([
              formatMonthLabel(s.month),
              ut.charAt(0).toUpperCase() + ut.slice(1),
              String(s.count || 0),
              fmtNumber(s.totalUsage || 0) + ' ' + (s.unit || ''),
              fmtCurrency(s.totalCost || 0),
            ]);
          });
        });

        autoTable(doc, {
          head: [['Month', 'Utility', 'Bills', 'Usage', 'Cost']],
          body: detailRows,
          startY: dtCard.contentY,
          margin: { left: dtCard.contentX, right: PAGE.w - (dtCard.contentX + dtCard.contentW) },
          theme: 'grid',
          headStyles: { fillColor: BRAND.primary, textColor: '#ffffff', fontStyle: 'bold', fontSize: 8, cellPadding: 2 },
          bodyStyles: { fontSize: 7.5, cellPadding: 2 },
          alternateRowStyles: { fillColor: BRAND.grayLight },
          columnStyles: {
            0: { cellWidth: 25 },
            1: { cellWidth: 30 },
            2: { cellWidth: 18, halign: 'center' },
            3: { cellWidth: 45, halign: 'right' },
            4: { cellWidth: 35, halign: 'right' },
          },
        });
      }
    }

    // ── Add page numbers ──
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      drawPageFooter(doc, i, totalPages);
    }

    onProgress?.('Saving PDF...');
    doc.save(`UtiliTrack_Bento_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    onProgress?.(null);

    return true;
  }, [facilities, monthlyStats, onProgress]);

  // Expose method via ref
  React.useImperativeHandle(ref, () => ({
    generateBentoPDF,
  }), [generateBentoPDF]);

  // No offscreen rendering needed — charts are drawn directly in PDF
  return null;
});

BentoReportExporter.displayName = 'BentoReportExporter';

export default BentoReportExporter;
